import { parseOBJ, parsePLY, parseSTL, type Mesh } from "@/lib/mesh3d";

export type MeshFormat = "stl" | "obj" | "ply" | "glb" | "3mf";
export const MESH_ACCEPT = ".stl,.obj,.ply,.glb,.3mf";

export function detectMeshFormat(name: string): MeshFormat | null {
  const e = name.split(".").pop()?.toLowerCase();
  return e === "stl" || e === "obj" || e === "ply" || e === "glb" || e === "3mf" ? e : null;
}

/** Reads a mesh file. GLB/glTF goes through three.js (loaded only when needed); the others use our own parsers. */
export async function loadMeshFile(file: File, format: MeshFormat): Promise<Mesh> {
  const buf = await file.arrayBuffer();
  if (format === "stl") return parseSTL(buf);
  if (format === "obj") return parseOBJ(new TextDecoder().decode(buf));
  if (format === "ply") return parsePLY(buf);
  if (format === "3mf") return (await import("@/lib/threemf")).parse3MF(buf); // always millimetres
  const [{ GLTFLoader }, THREE] = await Promise.all([import("three/examples/jsm/loaders/GLTFLoader.js"), import("three")]);
  const gltf = await new Promise<{ scene: import("three").Group }>((resolve, reject) => new GLTFLoader().parse(buf, "", resolve as never, reject));
  gltf.scene.updateMatrixWorld(true);
  const vertices: number[] = [];
  const triangles: number[][] = [];
  const v = new THREE.Vector3();
  gltf.scene.traverse((o) => {
    const m = o as import("three").Mesh;
    if (!m.isMesh || !m.geometry) return;
    const pos = m.geometry.getAttribute("position");
    if (!pos) return;
    const base = vertices.length / 3;
    for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld); vertices.push(v.x, v.y, v.z); }
    const idx = m.geometry.getIndex();
    const n = idx ? idx.count : pos.count;
    for (let i = 0; i + 2 < n; i += 3) triangles.push(idx ? [base + idx.getX(i), base + idx.getX(i + 1), base + idx.getX(i + 2)] : [base + i, base + i + 1, base + i + 2]);
  });
  if (!vertices.length) throw new Error("No triangle geometry found in this GLB");
  return { vertices, triangles };
}

export const UNIT_FACTORS_TO_METRES: Record<string, { label: string; f: number }> = {
  mm: { label: "Millimetres", f: 0.001 },
  cm: { label: "Centimetres", f: 0.01 },
  m: { label: "Metres", f: 1 },
  in: { label: "Inches", f: 0.0254 },
};

/** Largest bounding-box side of a mesh in its own (unknown) units; 0 for an empty mesh. */
export function maxExtent(mesh: Mesh): number {
  const v = mesh.vertices;
  if (v.length < 3) return 0;
  let lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < v.length; i += 3) for (let k = 0; k < 3; k++) { const c = v[i + k]; if (c < lo[k]) lo[k] = c; if (c > hi[k]) hi[k] = c; }
  return Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]);
}

/**
 * Guess the units of a unitless OBJ/PLY from its size. Hobbyist and CAD exports are mostly millimetres,
 * so a largest side under 1000 units is taken as mm (a 1000 m object is implausible, a 1000 mm one is
 * a 1 m print); anything bigger or unmeasurable is taken as metres. It is only a default: the user can change it.
 */
export function guessUnits(mesh: Mesh): "mm" | "m" {
  const m = maxExtent(mesh);
  return m > 0 && m < 1000 ? "mm" : "m";
}

/** Plain-English reason a parsed mesh is unusable, or null when it has geometry. */
export function meshProblem(mesh: Mesh): string | null {
  if (mesh.vertices.length === 0 || mesh.triangles.length === 0) return "No triangles found in this file. It may be empty, corrupt, or not the format its extension says.";
  if (mesh.vertices.some((n) => !Number.isFinite(n))) return "This file contains invalid coordinates (NaN), so it can't be converted.";
  return null;
}
